import { Icons } from "@/components/homepage/ui/Icons";
import { cn } from "@/lib/utils";
import { PLANS } from "@/lib/config/plans";
import { SUPPORT_EMAIL } from "@/lib/config/site";

export const Highlight = ({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) => {
    return (
        <span
            className={cn(
                "bg-radial from-gradient-primary to-gradient-secondary/40 bg-clip-text text-transparent",
                className,
            )}
        >
            {children}
        </span>
    );
};

export const BLUR_FADE_DELAY = 0.15;

export const siteConfig = {
    name: "Footage",
    description: "AI-powered video creation studio",
    cta: "Get Started",
    keywords: [
        "AI Video Creation",
        "AI Video Generation",
        "AI Creative Direction",
        "AI Video Editing",
    ],
    links: {
        email: SUPPORT_EMAIL,
        twitter: "https://twitter.com/footageapp",
        discord: "https://discord.gg/footage",
        github: "https://github.com/footage",
        instagram: "https://instagram.com/footageapp",
    },
    nav: {
        links: [
            { id: 1, name: "Solutions", href: "/solutions" },
            { id: 2, name: "Pricing", href: "/pricing" },
            { id: 5, name: "Integrations", href: "/integrations" },
            { id: 6, name: "Docs", href: "/docs" },
            { id: 3, name: "Support", href: "/support" },
            { id: 4, name: "Download", href: "/download-for-mac" },
        ],
    },
    hero: {
        badgeIcon: null,
        badge: "",
        title: (<>Create videos with AI</>),
        description:
            "Footage writes creative briefs, generates clips, and refines every frame so you can focus on your creative vision.",
        cta: {
            primary: {
                text: "Get started – it's free",
                href: "/login",
            },
        },
    },
    demoSection: {
        title: "Simple. Seamless. Smart.",
        description:
            "Discover how Footage transforms your ideas into stunning videos in four easy steps",
        items: [
            {
                id: 1,
                title: "Describe Your Vision",
                content:
                    "Tell Footage what you want to create. Describe your concept, mood, and style — our AI understands your creative intent instantly.",
                image:
                    "https://images.unsplash.com/photo-1720371300677-ba4838fa0678?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
            },
            {
                id: 2,
                title: "Generate",
                content:
                    "AI generates professional video clips from your descriptions. Choose from multiple AI models to match your creative style.",
                image:
                    "https://images.unsplash.com/photo-1686170287433-c95faf6d3608?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxmZWF0dXJlZC1waG90b3MtZmVlZHwzfHx8ZW58MHx8fHx8fA%3D%3D",
            },
            {
                id: 3,
                title: "Edit",
                content:
                    "Arrange clips on a visual timeline, refine prompts, and regenerate until every frame is perfect.",
                image:
                    "https://images.unsplash.com/photo-1720378042271-60aff1e1c538?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxmZWF0dXJlZC1waG90b3MtZmVlZHwxMHx8fGVufDB8fHx8fA%3D%3D",
            },
            {
                id: 4,
                title: "Share",
                content:
                    "Export your finished video and share it with the world. Professional quality, ready for any platform.",
                image:
                    "https://images.unsplash.com/photo-1666882990322-e7f3b8df4f75?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfDF8fHxlbnwwfHx8fHw%3D",
            },
        ],
    },
    companyShowcase: {
        companyLogos: [
            {
                id: 1,
                name: "Company 1",
                logo: (
                    <svg
                        width="100"
                        height="24"
                        viewBox="0 0 100 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="dark:fill-white fill-black"
                    >
                        <text x="0" y="18" fontSize="16" fontWeight="600" fill="currentColor">Company 1</text>
                    </svg>
                ),
            },
            {
                id: 2,
                name: "Company 2",
                logo: (
                    <svg
                        width="100"
                        height="24"
                        viewBox="0 0 100 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="dark:fill-white fill-black"
                    >
                        <text x="0" y="18" fontSize="16" fontWeight="600" fill="currentColor">Company 2</text>
                    </svg>
                ),
            },
            {
                id: 3,
                name: "Company 3",
                logo: (
                    <svg
                        width="100"
                        height="24"
                        viewBox="0 0 100 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="dark:fill-white fill-black"
                    >
                        <text x="0" y="18" fontSize="16" fontWeight="600" fill="currentColor">Company 3</text>
                    </svg>
                ),
            },
            {
                id: 4,
                name: "Company 4",
                logo: (
                    <svg
                        width="100"
                        height="24"
                        viewBox="0 0 100 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="dark:fill-white fill-black"
                    >
                        <text x="0" y="18" fontSize="16" fontWeight="600" fill="currentColor">Company 4</text>
                    </svg>
                ),
            },
        ],
    },
    workflowSection: {
        badge: {
            icon: <Icons.sparkles className="size-4 text-muted-foreground" />,
            text: "Create",
        },
        title: (<>Your AI partner for {" "}<Highlight>every video</Highlight></>),
        description: "Footage writes creative briefs, generates clips, and refines every frame so you can focus on storytelling and vision",
        sections: {
            title: "AI meets your creative workflow",
            description: "Start creating videos by describing your vision. Review AI-generated clips and refine them with a single click.",
            ctaButton: {
                text: "Learn More",
                href: "#",
            },
            blocks: [
                {
                    id: 1,
                    icon: <Icons.sparkles className="size-4 text-muted-foreground" />,
                    title: "Describe your vision and let AI create",
                    description: "Tell Footage what you want to see. Your AI creative partner analyzes your concept and generates stunning video clips right away.",
                },
                {
                    id: 2,
                    icon: <Icons.video className="size-4 text-muted-foreground" />,
                    title: "Review and refine your clips",
                    description: "Get instant AI-generated video clips with visual previews. Refine prompts and regenerate with a single click to perfect your content.",
                },
            ],
        }
    },
    workflowConnectSection: {
        title: "Edit. Refine. Perfect.",
        description: "Arrange clips on a visual timeline, fine-tune every frame, and let AI handle the heavy lifting of video production.",
        ctaButton: {
            text: "Learn More",
            href: "#",
        },
        blocks: [
            {
                id: 1,
                icon: <Icons.timeline className="size-4 text-muted-foreground" />,
                title: "Timeline editing made simple",
                description: "Drag and drop clips on your timeline. Reorder, trim, and arrange your video with an intuitive visual editor.",
            },
            {
                id: 2,
                icon: <Icons.sparkles className="size-4 text-muted-foreground" />,
                title: "AI-powered refinement",
                description: "Refine any clip with natural language. Describe changes and let AI regenerate until every frame matches your vision.",
            },
        ],
    },
    featureSection: {
        badge: {
            icon: <Icons.wrench className="size-4 text-muted-foreground" />,
            text: "Engineered for scale",
        },
        title: (<>Stop staring at blank timelines.{" "}<Highlight>Start creating.</Highlight></>),
        description: "Your AI creative director handles shot composition, visual style, and production details. Spend time on storytelling, not rendering.",
        sections: {
            title: "Built for creators scaling at warp speed",
            description: "From solo creators to production studios. Trusted by teams that need to ship content fast and stay creative.",
            ctaButton: {
                text: "Learn More",
                href: "#",
            },
            blocks: [
                {
                    id: 1,
                    icon: <Icons.video className="size-4 text-muted-foreground" />,
                    title: "Used by creators worldwide",
                    description: "Trusted by creators and production teams at rapidly growing studios. Create professional video content 10x faster while maintaining quality.",
                },
                {
                    id: 2,
                    icon: <Icons.sparkles className="size-4 text-muted-foreground" />,
                    title: "Active in 130+ countries worldwide",
                    description: "Creators choose Footage to accelerate video production. Join the global movement toward AI-powered content creation.",
                },
            ],
        }
    },
    connectSection: {
        badge: {
            icon: <Icons.sparkles className="size-4 text-muted-foreground" />,
            text: "Scale",
        },
        title: (<>Create. Edit.{" "}<Highlight>Share.</Highlight></>),
        description: "Collaborate seamlessly with AI to produce stunning video content",
        step1: {
            title: "Describe your vision",
            description: "Tell Footage what you want to create. Describe your concept, characters, and mood. Your AI creative partner understands context and brings your ideas to life.",
        },
        step2: {
            title: "Review AI-generated clips",
            description: "Watch AI-generated video clips appear in real time. Preview each scene, compare variations, and select the best takes for your project.",
        },
        step3: {
            title: "Edit and share your videos",
            description: "Arrange clips on your timeline, refine with natural language, and export your finished video. Share professional content ready for any platform.",
        },
    },
    testimonialSection: {
        badge: {
            icon: (
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-muted-foreground"
                >
                    <path
                        d="M4 4C3.44772 4 3 4.44772 3 5V7C3 7.55228 3.44772 8 4 8H5V10C5 10.5523 5.44772 11 6 11H7C7.55228 11 8 10.5523 8 10V5C8 4.44772 7.55228 4 7 4H4Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M11 4C10.4477 4 10 4.44772 10 5V7C10 7.55228 10.4477 8 11 8H12V10C12 10.5523 12.4477 11 13 11H14C14.5523 11 15 10.5523 15 10V5C15 4.44772 14.5523 4 14 4H11Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            ),
            text: "Testimonials",
        },
        title: (<>Hear from our <Highlight>creator</Highlight> community</>),
        description: "Thousands of creators trust Footage daily. Discover how teams and individuals produce stunning video content faster than ever.",
        testimonials: [
            {
                id: "1",
                name: "Alex Rivera",
                role: "Creative Director at StudioX",
                img: "https://randomuser.me/api/portraits/men/91.jpg",
                description: (
                    <p>
                        Footage has completely transformed our video production pipeline.
                        What used to take days now takes minutes.
                        The AI understands exactly what we need.
                    </p>
                ),
            },
            {
                id: "2",
                name: "Samantha Lee",
                role: "Content Lead at BrightMedia",
                img: "https://randomuser.me/api/portraits/women/12.jpg",
                description: (
                    <p>
                        We went from brainstorming to finished video in under an hour.
                        The AI-generated clips are incredibly high quality.
                        Our engagement rates have doubled since switching.
                    </p>
                ),
            },
            {
                id: "3",
                name: "Raj Patel",
                role: "Founder at VisualFlow",
                img: "https://randomuser.me/api/portraits/men/45.jpg",
                description: (
                    <p>
                        As a startup, we needed professional video content without a production budget.
                        Footage delivers studio-quality results at a fraction of the cost.
                        Essential tool for any growing brand.
                    </p>
                ),
            },
            {
                id: "4",
                name: "Emily Chen",
                role: "Product Manager at Streamline",
                img: "https://randomuser.me/api/portraits/women/83.jpg",
                description: (
                    <p>
                        The timeline editing and AI refinement workflow is incredibly intuitive.
                        Our team creates polished product demos in minutes.
                        <Highlight>A must-have for product marketing teams.</Highlight>
                    </p>
                ),
            },
            {
                id: "5",
                name: "Michael Brown",
                role: "Video Producer at ContentForge",
                img: "https://randomuser.me/api/portraits/men/1.jpg",
                description: (
                    <p>
                        I was skeptical about AI video at first, but Footage changed my mind completely.
                        The quality and creative control are remarkable.
                        <Highlight>It feels like having a full production team at your fingertips.</Highlight>
                    </p>
                ),
            },
            {
                id: "6",
                name: "Linda Wu",
                role: "Marketing VP at LaunchPad",
                img: "https://randomuser.me/api/portraits/women/5.jpg",
                description: (
                    <p>
                        Footage cut our video production costs by 80%.
                        <Highlight>
                            The AI understands brand consistency and delivers on-point creative every time.
                        </Highlight>{" "}
                    </p>
                ),
            },
            {
                id: "7",
                name: "Carlos Gomez",
                role: "Head of Content at Elevate",
                img: "https://randomuser.me/api/portraits/men/14.jpg",
                description: (
                    <p>
                        We produce 10x more video content now with a smaller team.
                        <Highlight>
                            The AI creative direction is genuinely impressive.
                        </Highlight>{" "}
                        Footage is the future of content creation.
                    </p>
                ),
            },
            {
                id: "8",
                name: "Aisha Khan",
                role: "Social Media Director at TrendWave",
                img: "https://randomuser.me/api/portraits/women/56.jpg",
                description: (
                    <p>
                        Our social media video output went from 2 per week to 10.
                        <Highlight>
                            Footage makes it effortless to create scroll-stopping content.
                        </Highlight>{" "}
                        Game-changer for social teams.
                    </p>
                ),
            },
            {
                id: "9",
                name: "Tom Chen",
                role: "Director of Creative at MediaHub",
                img: "https://randomuser.me/api/portraits/men/18.jpg",
                description: (
                    <p>
                        The ability to describe a scene in natural language and get a polished video clip is magical.
                        <Highlight>
                            Footage bridges the gap between imagination and execution.
                        </Highlight>{" "}
                        A milestone in creative tooling.
                    </p>
                ),
            },
            {
                id: "10",
                name: "Sofia Patel",
                role: "CEO at CreatorStudio",
                img: "https://randomuser.me/api/portraits/women/73.jpg",
                description: (
                    <p>
                        Footage democratizes professional video production.
                        <Highlight>
                            Anyone with an idea can now create stunning video content.
                        </Highlight>{" "}
                        Transforming the creative landscape.
                    </p>
                ),
            },
        ],
    },
    pricing: {
        title: "Simple pricing for everyone",
        description:
            "Choose the plan that works for you",
        pricingItems: [
            {
                name: PLANS.free.name,
                href: "/login",
                price: `$${PLANS.free.price}`,
                period: "month",
                yearlyPrice: `$${PLANS.free.price}`,
                features: PLANS.free.features,
                description: PLANS.free.description,
                buttonText: "Start Free",
                buttonColor: "bg-accent text-primary",
                isPopular: false,
            },
            {
                name: PLANS.pro.name,
                href: "/login",
                price: `$${PLANS.pro.price}`,
                period: "month",
                yearlyPrice: `$${PLANS.pro.price * 12}`,
                features: PLANS.pro.features,
                description: PLANS.pro.description,
                buttonText: "Upgrade to Pro",
                buttonColor: "bg-secondary text-white",
                isPopular: true,
            },
            {
                name: PLANS.max.name,
                href: "/login",
                price: `$${PLANS.max.price}`,
                period: "month",
                yearlyPrice: `$${PLANS.max.price * 12}`,
                features: PLANS.max.features,
                description: PLANS.max.description,
                buttonText: "Go Max",
                buttonColor: "bg-sky-500 text-white",
                isPopular: false,
            },
            {
                name: PLANS.ultra.name,
                href: "/login",
                price: `$${PLANS.ultra.price}`,
                period: "month",
                yearlyPrice: `$${PLANS.ultra.price * 12}`,
                features: PLANS.ultra.features,
                description: PLANS.ultra.description,
                buttonText: "Go Ultra",
                buttonColor: "bg-fuchsia-500 text-white",
                isPopular: false,
            },
        ],
    },
    faqSection: {
        title: "Frequently Asked Questions",
        description:
            "If you have any other questions, please don't hesitate to contact us.",
        faQitems: [
            {
                id: 1,
                question: "Is there a free plan?",
                answer:
                    "Yes! You can sign up and start creating for free. No credit card required.",
            },
            {
                id: 2,
                question: "How does usage work?",
                answer:
                    "Each plan includes a monthly usage allowance for AI-powered generation. Usage depends on the model and output type.",
            },
            {
                id: 3,
                question: "Can I cancel anytime?",
                answer:
                    "Absolutely. You can cancel your subscription at any time from your account settings. No questions asked.",
            },
            {
                id: 4,
                question: "What happens when I hit my usage limit?",
                answer:
                    "You can upgrade your plan for higher limits, or wait until your usage resets at the start of your next billing cycle.",
            },
            {
                id: 5,
                question: "What payment methods do you accept?",
                answer:
                    "We accept all major credit cards including Visa, Mastercard, and American Express. Payments are securely processed through Stripe.",
            },
            {
                id: 6,
                question: "What AI models does Footage use?",
                answer:
                    "Footage integrates multiple leading AI video generation models, giving you the flexibility to choose the style and quality that best fits your project. We continuously add new models as the technology evolves.",
            },
            {
                id: 7,
                question: "What kind of content can I create?",
                answer:
                    "We strictly prohibit the creation of harmful, illegal, pornographic, or NSFW content, including graphic violence and gore. Users who attempt to violate this policy will be permanently suspended according to our Terms of Service.",
            },
        ],
    },
    ctaSection: {
        id: "cta",
        title: "Ready to create your first video?",
        backgroundImage: "/agent-cta-background.png",
        button: {
            text: "Start for free",
            href: "/login",
        },
        subtext: "Stop staring at blank timelines. Let AI handle the creative heavy lifting. Start creating stunning videos today, completely free.",
    },
    footerLinks: [
        {
            title: "Product",
            links: [
                { id: 1, title: "Home", url: "/" },
                { id: 2, title: "Solutions", url: "/solutions" },
                { id: 17, title: "Assets", url: "/assets" },
                { id: 3, title: "Sign up", url: "/signup" },
                { id: 4, title: "Login", url: "/login" },
            ],
        },
        {
            title: "Resources",
            links: [
                { id: 5, title: "Pricing", url: "/pricing" },
                { id: 16, title: "Integrations", url: "/integrations" },
                { id: 20, title: "Documentation", url: "/docs" },
                { id: 19, title: "Footage CLI", url: "/cli" },
                { id: 6, title: "Support", url: "/support" },
                { id: 7, title: "Download for Mac", url: "/download-for-mac" },
                { id: 8, title: "Sitemap", url: "/sitemap.xml" },
            ],
        },
        {
            title: "Legal",
            links: [
                { id: 12, title: "Terms of Service", url: "/terms" },
                { id: 13, title: "Privacy Policy", url: "/privacy" },
                { id: 16, title: "Content Policy", url: "/content-policy" },
                { id: 14, title: "Contact Us", url: `mailto:${SUPPORT_EMAIL}` },
                { id: 15, title: "LLMs.txt", url: "/llms.txt" },
            ],
        },
    ],
    company: "Generative Platforms Inc.",
};

export type SiteConfig = typeof siteConfig;
